import React, { useEffect, useState } from 'react'
import { TAB_KEY_CONSTANTS, TabSplitsProps } from '../../utils'
import { TabSplitsTypes } from '../../utils/types/tabSplits.type'

const TabSplits: React.FC<TabSplitsProps> = ({
  tabs,
  activeTabKey,
  activeTabIndex,
  onTabChange,
  commonActionComponent,
  isShadow = false,
  fontSize = 'text-sm',
  tabsHeaderClassName = '',
}) => {
  // Set initial activeTab based on props
  const getInitialActiveTab = () => {
    if (activeTabKey !== undefined) {
      const index = tabs.findIndex((tab) => tab.key === activeTabKey)
      // If tab is disabled return to first enabled tab
      if (index !== -1 && tabs[index]?.disabled) {
        const firstEnabledIndex = getFirstEnabledTabIndex()
        if (firstEnabledIndex !== -1) {
          // Trigger tab change for tab to take time to render
          setTimeout(() => {
            if (onTabChange) {
              onTabChange({
                key: tabs[firstEnabledIndex].key,
                index: firstEnabledIndex,
              })
            }
          }, 1)

          return firstEnabledIndex
        }
      } else if (index !== -1) {
        return index
      } else {
        // Default to the first tab if key not found
        return 0
      }
      return index !== -1 ? index : 0
    } else if (activeTabIndex !== undefined) {
      return activeTabIndex
    }
    // Default to the first tab
    return 0
  }

  const getFirstEnabledTabIndex = () => {
    return tabs.findIndex((tab) => !tab.disabled)
  }

  const [activeTab, setActiveTab] = useState<number>(getInitialActiveTab)

  // Trigger Tab Change function on change of active tab
  const handleTabChange = (newIndex: number) => {
    if (!tabs[newIndex]?.disabled && newIndex !== activeTab) {
      setActiveTab(newIndex)
      if (onTabChange) {
        onTabChange({ key: tabs[newIndex].key, index: newIndex })
      }
    } else if (tabs[newIndex]?.disabled) {
      const firstEnabledIndex = getFirstEnabledTabIndex()
      if (firstEnabledIndex !== -1 && firstEnabledIndex !== activeTab) {
        setActiveTab(firstEnabledIndex)
        if (onTabChange) {
          onTabChange({
            key: tabs[firstEnabledIndex].key,
            index: firstEnabledIndex,
          })
        }
      }
    }
  }
  //  Handles tab accessibility
  const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
    if (tabs[index]?.disabled) return

    switch (event.key) {
      case TAB_KEY_CONSTANTS?.ARROW_RIGHT:
        handleTabChange((activeTab + 1) % tabs.length)
        break
      case TAB_KEY_CONSTANTS?.ARROW_LEFT:
        handleTabChange((activeTab - 1 + tabs.length) % tabs.length)
        break
      case TAB_KEY_CONSTANTS?.ENTER:
      case ' ':
        handleTabChange(index)
        break
      case TAB_KEY_CONSTANTS?.HOME:
        handleTabChange(0)
        break
      case TAB_KEY_CONSTANTS?.END:
        handleTabChange(tabs.length - 1)
        break
      default:
        break
    }
  }

  // Update internal state when controlled via props
  useEffect(() => {
    if (activeTabKey !== undefined) {
      const newIndex = tabs.findIndex((tab) => tab.key === activeTabKey)

      if (newIndex !== -1) setActiveTab(newIndex)
    } else if (activeTabIndex !== undefined) {
      setActiveTab(activeTabIndex)
    }
  }, [activeTabKey, activeTabIndex, tabs])

  return (
    <div className="h-full w-full flex flex-col">
      {/* Tabs Header */}
      <div
        role="tablist"
        aria-label="Tabs"
        className={`flex items-center justify-between pb-2 ${tabsHeaderClassName}`}
      >
        {/* Tabs List with Horizontal Scrolling */}
        <div className="flex items-center space-x-4 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200 mr-5">
          {tabs.map((tab: TabSplitsTypes, index: number) => (
            <div
              key={tab.key}
              id={`tab-${tab.key}`}
              role="tab"
              tabIndex={tab.disabled ? -1 : tab?.tabIndexForTabs ?? 0}
              aria-selected={activeTab === index}
              aria-controls={`tabpanel-${tab.key}`}
              onClick={() => !tab.disabled && handleTabChange(index)}
              onKeyDown={(event) =>
                !tab.disabled && handleKeyDown(event, index)
              }
              className={`flex items-center px-4 py-2 cursor-pointer whitespace-nowrap ${fontSize} ${
                tab.disabled
                  ? 'text-gray-400 cursor-not-allowed'
                  : activeTab === index
                  ? 'border-b-2 border-blue-500 font-semibold text-blue-500'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-qa-id={tab?.dataQaId}
            >
              {tab.icon && <div className="mr-2">{tab.icon}</div>}
              {tab.label}
            </div>
          ))}
        </div>
        {/* Action Components */}
        <div className="flex items-center space-x-2 flex-shrink-0">
          {tabs[activeTab]?.actionComponent || commonActionComponent || null}
        </div>
      </div>
      {/* Tab Content */}
      <div
        key={`tabpanel-${tabs[activeTab]?.key}`}
        id={`tabpanel-${tabs[activeTab]?.key}`}
        role="tabpanel"
        aria-labelledby={`tab-${tabs[activeTab]?.key}`}
        tabIndex={tabs[activeTab]?.tabIndexForTabs ?? 0}
        className={`${
          isShadow ? 'shadow-[0px_2px_4px_-1px_rgba(0,0,0,0.2)] ' : ''
        } flex-1 overflow-auto max-h-full h-full`}
      >
        {tabs[activeTab]?.content || <div>No content available</div>}
      </div>
    </div>
  )
}

export default TabSplits
